import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Button, Icon } from '@velove/design-system';
import { Col, Container, Row } from 'react-bootstrap';
import { Textarea } from 'theme-ui';
import { AppContext } from '../contexts/AppContext';
import { EntryType, DeliveryNotificationType, UserDeliveryRequirements } from '../contexts/contextTypes';
import { getFloorName } from '../utils/helpers';
import Switch from '../components/CustomerInputs/Switch';
import Radio from '../components/CustomerInputs/Radio';
import ThreeStageToggle from '../components/CustomerInputs/ThreeStageToggle';

// @ts-ignore
import Doorcode from '../assets/images/Doorcode.svg';
// @ts-ignore
import Intercom from '../assets/images/intercom.svg';
// @ts-ignore
import OpenDoor from '../assets/images/Open Door.svg';
// @ts-ignore
import NoDoorBell from '../assets/images/NoDoorBell.svg';
// @ts-ignore
import DoorBell from '../assets/images/DoorBell.svg';
// @ts-ignore
import SMS from '../assets/images/SMS.svg';
// @ts-ignore
import Quiet from '../assets/images/Quiet.svg';

const CustomerInputs = () => {
  const { packageId } = useParams();
  const [entry, setEntry] = useState({
    key: '',
    doorCode: '',
    intercom: ''
  });
  const [floor, setFloor] = useState<number | null>(null);
  const [direction, setDirection] = useState<string | null>(null);
  const [isOutside, setLeaveOutside] = useState(true);
  const [note, setNote] = useState('');
  const [nameOnDoor, setNameOnDoor] = useState('');
  const [deliveryNotificationType, setDeliveryNotificationType] = useState(DeliveryNotificationType.NONE);
  const { partner, settings, trackingDetails, setCustomerInputsOpen } = useContext(AppContext);
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (trackingDetails) {
      if (trackingDetails.recipient) {
        const { doorCode, intercom, entryOption } = trackingDetails.recipient;

        setEntry({
          key:
            entryOption === EntryType.DOORCODE
              ? 'doorCode'
              : entryOption === EntryType.INTERCOM
              ? 'intercom'
              : trackingDetails.task?.consumerUpdatedInfoAt
              ? doorCode
                ? 'doorCode'
                : intercom
                ? 'intercom'
                : 'none'
              : doorCode
              ? 'doorCode'
              : '',
          doorCode: doorCode || '',
          intercom: intercom || ''
        });

        if (trackingDetails?.recipient?.floorNo) {
          const floorNoString = String(trackingDetails.recipient.floorNo).trim();

          // Extract the numeric part (including negative numbers)
          const numericMatch = floorNoString.match(/-?\d+/);
          const floorNumber = numericMatch ? Number(numericMatch[0]) : null;
          setFloor(floorNumber);

          // Extract direction for Denmark only
          if (partner?.countryCode === 'dk') {
            // Match common Danish floor directions: MF, TV, TH.
            // Can be space-separated (e.g., "2 MF") or attached (e.g., "4TV", "5TH")
            const directionMatch = floorNoString.match(/\d+\s*(MF|TV|TH)|^(MF|TV|TH)/i);
            if (directionMatch) {
              setDirection((directionMatch[1] || directionMatch[2]).toUpperCase());
            } else {
              setDirection('MF');
            }
          }
        }
      }

      setNameOnDoor(trackingDetails?.task?.nameOnDoor || '');
      setNote(trackingDetails?.task?.taskInstructions || '');
      setLeaveOutside(trackingDetails?.package?.userDeliveryRequirements === UserDeliveryRequirements.CONTACTLESS);

      if (trackingDetails?.package?.deliveryNotificationType) {
        setDeliveryNotificationType(trackingDetails?.package?.deliveryNotificationType);
      } else if (trackingDetails?.package?.userDeliveryRequirements !== UserDeliveryRequirements.CONTACTLESS) {
        setDeliveryNotificationType(DeliveryNotificationType.RING_DOOR_BELL);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingDetails]);

  const deliveryInstructionMutation = useMutation({
    mutationFn: (deliveryData: {
      floorNo: string | null;
      doorCode: string | null;
      intercom: string | null;
      nameOnDoor: string | null;
      userDeliveryRequirements: string;
      deliveryInstructions: string;
      entryOption: EntryType | null;
      deliveryNotificationType: DeliveryNotificationType | null;
    }) => {
      return axios.post(`${process.env.REACT_APP_API_ENDPOINT}/v1/package/${packageId}`, deliveryData, {
        headers: {
          'content-type': 'application/json',
          accept: 'application/json'
        }
      });
    },
    onSuccess: () => setCustomerInputsOpen(),
    onError: () => console.log('Update package info failed')
  });

  const isDisabled = () => {
    if (!entry.key) {
      return true;
    }
    if (entry.key === 'doorCode' && entry.doorCode.length === 0) {
      return true;
    }
    if (entry.key === 'intercom' && entry.intercom.length === 0) {
      return true;
    }
    if (floor === null) {
      return true;
    }

    return false;
  };

  return (
    <Container>
      <Row className="delivery_form--body">
        <Col>
          <Row>
            <Col>
              <h4 className="delivery_form--text">{t('customerInputs.enter_label')}</h4>
              <div className="delivery_form--entry_content">
                <Row>
                  <Col xxs={2} sm={2} md={2}>
                    <Radio
                      checked={entry.key === 'doorCode'}
                      handleCheck={() =>
                        setEntry({
                          ...entry,
                          key: 'doorCode'
                        })
                      }
                    />
                  </Col>
                  <Col xxs={8} sm={8} md={8} align="center">
                    <h5 className="delivery_form--entry-text">{t('customerInputs.door_code')}</h5>
                  </Col>
                  <Col xxs={2} sm={2} md={2} align="center">
                    <img className="delivery_form--entry-icon" src={Doorcode} alt="delivery_form_doorcode" />
                  </Col>
                  {entry.key === 'doorCode' ? (
                    <Col xxs={12} sm={12} md={12}>
                      <input
                        value={entry.doorCode}
                        className="delivery_form--entry-input"
                        onChange={(event) => {
                          setEntry({ ...entry, doorCode: event.target.value });
                        }}
                        placeholder={'****'}
                        autoFocus={entry.key === 'doorCode'}
                        maxLength={10}
                      />
                    </Col>
                  ) : null}
                  <Col xxs={12} sm={12} md={12}>
                    <hr className="delivery_form--entry-separator" />
                  </Col>
                </Row>
                <Row>
                  <Col xxs={2} sm={2} md={2}>
                    <Radio
                      checked={entry.key === 'intercom'}
                      handleCheck={() =>
                        setEntry({
                          ...entry,
                          key: 'intercom'
                        })
                      }
                    />
                  </Col>
                  <Col xxs={8} sm={8} md={8}>
                    <h5 className="delivery_form--entry-text">{t('customerInputs.intercom')}</h5>
                  </Col>
                  <Col xxs={2} sm={2} md={2} align="center">
                    <img className="delivery_form--entry-icon" src={Intercom} alt="delivery_form_intercom" />
                  </Col>
                  {entry.key === 'intercom' ? (
                    <Col xxs={12} sm={12} md={12}>
                      <input
                        value={entry.intercom}
                        className="delivery_form--entry-input"
                        onChange={(event) => {
                          setEntry({ ...entry, intercom: event.target.value });
                        }}
                        placeholder={'****'}
                        autoFocus={entry.key === 'intercom'}
                        maxLength={30}
                      />
                    </Col>
                  ) : null}
                  <Col xxs={12} sm={12} md={12}>
                    <hr className="delivery_form--entry-separator" />
                  </Col>
                </Row>
                <Row>
                  <Col xxs={2} sm={2} md={2}>
                    <Radio
                      checked={entry.key === 'none'}
                      handleCheck={() =>
                        setEntry({
                          ...entry,
                          key: 'none'
                        })
                      }
                    />
                  </Col>
                  <Col xxs={8} sm={8} md={8}>
                    <h5 className="delivery_form--entry-text">{t('customerInputs.no_entry')}</h5>
                  </Col>
                  <Col xxs={2} sm={2} md={2} align="center">
                    <img className="delivery_form--entry-icon" src={OpenDoor} alt="delivery_form_none" />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Row className="delivery_form--count_content">
            <h4 className="delivery_form--text">{t('customerInputs.name_on_door')}</h4>
            <Col xxs={12} sm={12} md={12}>
              <input
                value={nameOnDoor}
                className="delivery_form--entry-input"
                onChange={(event) => {
                  setNameOnDoor(event.target.value);
                }}
                placeholder={'C/O'}
                maxLength={40}
              />
            </Col>
          </Row>
          <Row className="delivery_form--count_content">
            <h4 className="delivery_form--text">{t('customerInputs.floor_label')}</h4>
            <Col xxs={3} sm={3} md={3} align="center">
              <Button className="delivery_form--count-button" color="black" onClick={() => setFloor((floor === null ? 1 : floor) - 1)}>
                <Icon icon="Minus" />
              </Button>
            </Col>
            <Col xxs={6} sm={6} md={6} align="center">
              <label className="delivery_form--count-input">{floor || floor === 0 ? getFloorName(floor, settings) : t('customerInputs.floor')}</label>
            </Col>
            <Col xxs={3} sm={3} md={3} align="center">
              <Button className="delivery_form--count-button" color="black" onClick={() => setFloor((floor === null ? -1 : floor) + 1)}>
                <Icon icon="Plus" />
              </Button>
            </Col>
          </Row>
          {settings && partner?.countryCode === 'dk' && (
            <Row className="delivery_form--count_content">
              <Col xxs={12} sm={12} md={12} align="center">
                <ThreeStageToggle value={direction} onChange={setDirection} />
              </Col>
            </Row>
          )}
          {!(
            trackingDetails?.task?.identificationCheckRequired ||
            trackingDetails?.task?.recipientMinimumAge ||
            trackingDetails?.task?.recipientMustMatchEndCustomer ||
            trackingDetails?.task?.requireSignature
          ) && (
            <Row className="delivery_form--toggle_content">
              <Col>
                <h5 className="delivery_form--text">{t('customerInputs.leave_at_door_label')}</h5>
                <Row>
                  <Col align="center">
                    <h5 className="delivery_form--toggle-text">{t('customerInputs.no')}</h5>
                  </Col>
                  <Col align="center">
                    <Switch isToggle={isOutside} handleToggle={() => setLeaveOutside(!isOutside)} />
                  </Col>
                  <Col align="center">
                    <h5 className="delivery_form--toggle-text">{t('customerInputs.yes')}</h5>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          <Row className="delivery_form--toggle_content">
            <Col>
              <h5 className="delivery_form--text" style={{ marginTop: '15px' }}>
                {t('customerInputs.contactless_delivery_type_label')}
              </h5>
              <Row
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '15px'
                }}
              >
                {isOutside ? (
                  <Col xxs={4} sm={4} md={4} align="center">
                    <Button
                      className="delivered-type--button"
                      onClick={() => setDeliveryNotificationType(DeliveryNotificationType.NONE)}
                      style={{
                        ...(deliveryNotificationType === DeliveryNotificationType.NONE && {
                          backgroundColor: '#969595'
                        })
                      }}
                    >
                      <img className="delivery_form--contactless-icon" src={NoDoorBell} alt={`delivery_form--contactless_none`} />
                      <p
                        style={{
                          color: 'black',
                          padding: 0,
                          margin: 0
                        }}
                      >
                        {t('customerInputs.none')}
                      </p>
                    </Button>
                  </Col>
                ) : null}
                <Col xxs={isOutside ? 4 : 6} sm={isOutside ? 4 : 6} md={isOutside ? 4 : 6} align="center">
                  <Button
                    className="delivered-type--button"
                    onClick={() => setDeliveryNotificationType(DeliveryNotificationType.RING_DOOR_BELL)}
                    style={{
                      ...(deliveryNotificationType === DeliveryNotificationType.RING_DOOR_BELL && {
                        backgroundColor: '#969595'
                      })
                    }}
                  >
                    <img className="delivery_form--contactless-icon" src={DoorBell} alt={`delivery_form--ring_door_bell`} />
                    <p
                      style={{
                        color: 'black',
                        padding: 0,
                        margin: 0
                      }}
                    >
                      {t('customerInputs.ring_bell')}
                    </p>
                  </Button>
                </Col>
                {isOutside ? (
                  <Col xxs={4} sm={4} md={4} align="center">
                    <Button
                      className="delivered-type--button"
                      onClick={() => setDeliveryNotificationType(DeliveryNotificationType.SEND_SMS)}
                      style={{
                        ...(deliveryNotificationType === DeliveryNotificationType.SEND_SMS && {
                          backgroundColor: '#969595'
                        })
                      }}
                    >
                      <img className="delivery_form--contactless-icon" src={SMS} alt={`delivery_form--sms`} />
                      <p style={{ color: 'black', padding: 0, margin: 0 }}>SMS</p>
                    </Button>
                  </Col>
                ) : (
                  <Col xxs={6} sm={6} md={6} align="center">
                    <Button
                      className="delivered-type--button"
                      onClick={() => setDeliveryNotificationType(DeliveryNotificationType.QUIET)}
                      style={{
                        ...(deliveryNotificationType === DeliveryNotificationType.QUIET && {
                          backgroundColor: '#969595'
                        })
                      }}
                    >
                      <img className="delivery_form--contactless-icon" src={Quiet} alt={`delivery_form--quite`} />
                      <p
                        style={{
                          color: 'black',
                          padding: 0,
                          margin: 0
                        }}
                      >
                        {t('customerInputs.quiet')}
                      </p>
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row>
            <Col>
              <h5 className="delivery_form--note-label">{t('customerInputs.note')}</h5>
              <Textarea
                id="note"
                rows={5}
                variant="noShadow"
                className="delivery_form--text-area"
                value={note}
                placeholder={t('customerInputs.note_placeholder') || 'Note'}
                onChange={(event) => {
                  const value = event.target.value;
                  setNote(value.substring(0, 100));
                }}
              />
            </Col>
          </Row>
          <Button
            className="delivery_form--button"
            style={{
              opacity: isDisabled() || deliveryInstructionMutation.isLoading ? 0.3 : 1
            }}
            onClick={() => {
              localStorage.setItem('scrollPosition', '0');
              deliveryInstructionMutation.mutate({
                floorNo: floor || floor === 0 ? `${floor.toString()}${direction ? ` ${direction}` : ''}` : null,
                doorCode: entry.key === 'doorCode' ? entry.doorCode : null,
                intercom: entry.key === 'intercom' ? entry.intercom : null,
                nameOnDoor: nameOnDoor ? nameOnDoor : null,
                userDeliveryRequirements: isOutside ? UserDeliveryRequirements.CONTACTLESS : UserDeliveryRequirements.IN_PERSON,
                deliveryInstructions: note,
                entryOption: entry.key === 'doorCode' ? EntryType.DOORCODE : entry.key === 'intercom' ? EntryType.INTERCOM : null,
                deliveryNotificationType: deliveryNotificationType
              });
            }}
            disabled={isDisabled()}
            loading={deliveryInstructionMutation.isLoading}
          >
            {isDisabled() ? t('customerInputs.warning') : t('customerInputs.done')}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default CustomerInputs;
